import { ClipboardEvent, FormEvent, useRef, useState } from "react";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { AddPhotoAlternate, ArrowBack, Close } from "@mui/icons-material";

import { useAuth } from "features/store";
import { describeClient } from "features/support/clientInfo";
import {
  PreparedScreenshot,
  prepareScreenshot,
} from "features/support/screenshots";
import {
  TICKET_DESCRIPTION_MAX_LENGTH,
  TICKET_MAX_SCREENSHOTS,
  TicketType,
  useCreateTicketMutation,
} from "features/support/tickets";

const FORM_ID = "new-ticket-form";
const ACCEPT = "image/png,image/jpeg,image/webp";

interface NewTicketFormProps {
  onCancel: () => void;
  onCreated: (id: number) => void;
}

interface ServerErrors {
  description?: string;
  form?: string;
}

/** Every string in a DRF error body, however deeply a field nests them. */
const collectMessages = (value: unknown): string[] => {
  if (typeof value === "string") {
    return [value];
  }
  if (Array.isArray(value)) {
    return value.flatMap(collectMessages);
  }
  if (typeof value === "object" && value !== null) {
    return Object.values(value).flatMap(collectMessages);
  }
  return [];
};

/**
 * DRF answers a rejected create with `{field: [messages]}`. The description's
 * own message sits under its field; every other field has no input of its own
 * to sit under, so it is named in the form-level alert.
 */
const toServerErrors = (error: unknown): ServerErrors => {
  const data =
    typeof error === "object" && error !== null && "data" in error
      ? (error as { data: unknown }).data
      : undefined;
  if (typeof data !== "object" || data === null || Array.isArray(data)) {
    return { form: "Failed to submit the ticket." };
  }
  const errors: ServerErrors = {};
  const others: string[] = [];
  Object.entries(data).forEach(([field, value]) => {
    const messages = collectMessages(value);
    if (field === "description") {
      errors.description = messages[0];
    } else if (field === "non_field_errors" || field === "detail") {
      others.push(...messages);
    } else {
      others.push(...messages.map((message) => `${field}: ${message}`));
    }
  });
  if (others.length) {
    errors.form = others.join(" ");
  }
  if (!errors.description && !errors.form) {
    errors.form = "Failed to submit the ticket.";
  }
  return errors;
};

const NewTicketForm = ({ onCancel, onCreated }: NewTicketFormProps) => {
  const { user } = useAuth();
  const [createTicket, { isLoading: isSubmitting }] = useCreateTicketMutation();

  const [type, setType] = useState<TicketType>("Bug");
  const [description, setDescription] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [screenshots, setScreenshots] = useState<PreparedScreenshot[]>([]);
  const [preparingCount, setPreparingCount] = useState(0);
  const [screenshotErrors, setScreenshotErrors] = useState<string[]>([]);
  const [serverErrors, setServerErrors] = useState<ServerErrors>({});
  const fileInput = useRef<HTMLInputElement>(null);

  const slotsTaken = screenshots.length + preparingCount;
  const isFull = slotsTaken >= TICKET_MAX_SCREENSHOTS;

  const descriptionError =
    description.length > TICKET_DESCRIPTION_MAX_LENGTH
      ? `Keep the description to ${TICKET_DESCRIPTION_MAX_LENGTH} characters.`
      : submitted && !description.trim()
        ? "Describe the issue or suggestion."
        : serverErrors.description;

  const addFiles = async (files: File[]) => {
    const room = TICKET_MAX_SCREENSHOTS - slotsTaken;
    const accepted = files.slice(0, Math.max(0, room));
    const errors =
      files.length > accepted.length
        ? [`Only ${TICKET_MAX_SCREENSHOTS} screenshots can be attached.`]
        : [];
    setPreparingCount((count) => count + accepted.length);
    // One at a time, so the thumbnails keep the order the files were given in.
    for (const file of accepted) {
      try {
        const prepared = await prepareScreenshot(file);
        setScreenshots((current) => [...current, prepared]);
      } catch (error) {
        errors.push(
          error instanceof Error ? error.message : "Could not add the image.",
        );
      } finally {
        setPreparingCount((count) => count - 1);
      }
    }
    setScreenshotErrors(errors);
  };

  // Pasting is how most screenshots arrive: a text paste is left alone, an
  // image paste becomes an attachment instead of being dropped.
  const handlePaste = (event: ClipboardEvent) => {
    const images = Array.from(event.clipboardData.files).filter((file) =>
      file.type.startsWith("image/"),
    );
    if (!images.length) {
      return;
    }
    event.preventDefault();
    void addFiles(images);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    setServerErrors({});
    if (
      !description.trim() ||
      description.length > TICKET_DESCRIPTION_MAX_LENGTH ||
      preparingCount
    ) {
      return;
    }
    const { operatingSystem, browser } = describeClient(navigator.userAgent);
    try {
      const ticket = await createTicket({
        type,
        description,
        // `user` is untyped in the auth slice, so its email is checked here.
        creator_email: typeof user?.email === "string" ? user.email : "",
        operating_system: operatingSystem,
        browser,
        screenshots: screenshots.map(({ filename, content_type, data }) => ({
          filename,
          content_type,
          data,
        })),
      }).unwrap();
      onCreated(ticket.id);
    } catch (error) {
      setServerErrors(toServerErrors(error));
    }
  };

  return (
    <>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <IconButton aria-label="Back to tickets" onClick={onCancel} edge="start">
          <ArrowBack />
        </IconButton>
        New ticket
      </DialogTitle>
      <DialogContent dividers onPaste={handlePaste}>
        <Box component="form" id={FORM_ID} noValidate onSubmit={handleSubmit}>
          <Stack spacing={2}>
            {serverErrors.form && (
              <Alert severity="error">{serverErrors.form}</Alert>
            )}
            <Tabs
              value={type}
              onChange={(_event, value: TicketType) => setType(value)}
              aria-label="Ticket type"
              sx={{ minHeight: 36 }}
            >
              <Tab label="Bug" value="Bug" sx={{ minHeight: 36, py: 0.5 }} />
              <Tab
                label="Suggestion"
                value="Suggestion"
                sx={{ minHeight: 36, py: 0.5 }}
              />
            </Tabs>
            <TextField
              label="Description"
              required
              multiline
              minRows={4}
              fullWidth
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              error={Boolean(descriptionError)}
              helperText={
                <Box
                  component="span"
                  display="flex"
                  justifyContent="space-between"
                  gap={2}
                >
                  <span>{descriptionError}</span>
                  <span>
                    {description.length}/{TICKET_DESCRIPTION_MAX_LENGTH}
                  </span>
                </Box>
              }
            />
            <Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Button
                  size="small"
                  startIcon={<AddPhotoAlternate />}
                  onClick={() => fileInput.current?.click()}
                  disabled={isFull}
                >
                  Add screenshot
                </Button>
                <Typography variant="caption" color="text.secondary">
                  {screenshots.length}/{TICKET_MAX_SCREENSHOTS} · or paste an
                  image
                </Typography>
                {!!preparingCount && <CircularProgress size={16} />}
              </Stack>
              <input
                ref={fileInput}
                type="file"
                accept={ACCEPT}
                multiple
                hidden
                data-testid="ticket-screenshot-input"
                onChange={(event) => {
                  const files = Array.from(event.target.files ?? []);
                  // Cleared so picking the same file again still fires.
                  event.target.value = "";
                  void addFiles(files);
                }}
              />
              {screenshotErrors.map((message) => (
                <Alert key={message} severity="warning" sx={{ mt: 1 }}>
                  {message}
                </Alert>
              ))}
              {!!screenshots.length && (
                <Stack direction="row" spacing={1} mt={1} flexWrap="wrap" useFlexGap>
                  {screenshots.map((screenshot, index) => (
                    // Index, not the data URL: the same image may be attached twice.
                    <Box key={index} position="relative">
                      <Box
                        component="img"
                        src={screenshot.previewUrl}
                        alt={screenshot.filename}
                        sx={{
                          width: 96,
                          height: 64,
                          objectFit: "cover",
                          borderRadius: 1,
                          border: 1,
                          borderColor: "divider",
                          display: "block",
                        }}
                      />
                      <IconButton
                        size="small"
                        aria-label={`Remove ${screenshot.filename}`}
                        onClick={() =>
                          setScreenshots((current) =>
                            current.filter((_item, other) => other !== index),
                          )
                        }
                        sx={{
                          position: "absolute",
                          top: -10,
                          right: -10,
                          bgcolor: "background.paper",
                          boxShadow: 1,
                          "&:hover": { bgcolor: "background.paper" },
                        }}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          type="submit"
          form={FORM_ID}
          variant="contained"
          disabled={isSubmitting || !!preparingCount}
          startIcon={
            isSubmitting ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          Submit
        </Button>
      </DialogActions>
    </>
  );
};

export default NewTicketForm;
